import type { ThreadRuntimeMaterialization } from "@/lib/thread-sync-runtime-materialization"

export type ThreadSyncRenderSource =
  | "durable_replica"
  | "runtime_checkpoint"
  | "optimistic_seed"
  | "empty"

export type ThreadSyncResumeSource =
  | "persisted_validated"
  | "runtime_checkpoint"
  | "none"

export type ThreadSyncActivationState =
  | "display_placeholder"
  | "authoritative_catchup"
  | "authoritative_live"
  | "known_empty"
  | "resyncing"

function hasPositiveCursor(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > 0
}

function hasHistoryToken(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0
}

export function resolvePersistedValidatedResumeSource(args: {
  cursor: number | null
  historyToken: string | null
}): ThreadSyncResumeSource {
  return hasPositiveCursor(args.cursor) && hasHistoryToken(args.historyToken)
    ? "persisted_validated"
    : "none"
}

export function deriveThreadSyncActivationState(args: {
  knownEmpty: boolean
  hasSnapshot: boolean
  hasVisibleConversation: boolean
  hasRuntimeConversation: boolean
  isCatchingUp: boolean
  runtimeMaterialization: ThreadRuntimeMaterialization
}): ThreadSyncActivationState {
  if (args.knownEmpty) {
    return "known_empty"
  }

  if (!args.hasSnapshot && args.hasVisibleConversation) {
    return "display_placeholder"
  }

  if (args.runtimeMaterialization === "optimistic_seed") {
    return "display_placeholder"
  }

  if (args.isCatchingUp && args.hasSnapshot && args.hasRuntimeConversation) {
    return "authoritative_catchup"
  }

  if (args.hasSnapshot && !args.hasVisibleConversation) {
    return "resyncing"
  }

  if (args.hasSnapshot && !args.isCatchingUp) {
    return "authoritative_live"
  }

  return "resyncing"
}
